/**
 * AppOperationsPage — the App Operations Board (ADR-075 step 5, D11).
 *
 * Per-app management surface in the vertical store for operators / tenant
 * admins (DoPlace operator for platform apps, OQ4b). Sections:
 *   1. Version history (deployment # + date primary, D8) with rollout status
 *      and the single-slot review queue made visible (D10)
 *   2. Development branches (dev-states from control-plane bookkeeping)
 *   3. App health + "Run verification"
 *   4. Change request / send-to-review triggers
 *   5. Access management — who is assigned to this app (tenant-scoped)
 *   6. Billing & subscription (read-only)
 *
 * ── decision-2026-08-02-tile-is-a-pointer-manage-is-the-workbench ────────────
 *
 * THIS PAGE IS THE WORKBENCH. The catalogue tile is a pointer to it and carries
 * Manage + Pin only. Edit description and Deploy rollout USED to be tile
 * actions; they are sections here now. Relocating a control does not
 * re-authorise it — each one keeps the predicate it always had:
 *
 *   App details / edit    isCatalogOperator, and the server still requires
 *                         REGISTRY_DEVELOPER_ROLES / REGISTRY_ADMIN_ROLES.
 *                         A tenant-admin sees the description READ-ONLY.
 *   Deploy a version      isCatalogOperator + the existing server gate.
 *   Assign users          board roles, bounded by the caller's tenant set.
 *   Billing               board roles, bounded by the caller's tenant set.
 *
 * Structurally absent here, by decision OQ1b: any approve / push-back control.
 * Approval stays a tenant-store action; this page renders decisions, never
 * makes them. Vocabulary is non-technical: the board persona sits between the
 * approver and the developer.
 *
 * Also structurally absent: DECOMMISSION. The danger zone at the bottom is a
 * reserved SLOT and nothing else — decommission is governed by
 * decision-2026-08-02-delete-ui-under-tile and built by its own brief. The slot
 * exists so that work lands here and never back on the tile.
 *
 * Route gate (UX only — every endpoint re-checks server-side) — TWO QUESTIONS,
 * both asked, since F6 / 2026-08-03:
 *   ROLE          tenant-admin / broker-admin, from auth/surfaceAccess
 *                 APP_OPERATIONS_ROLES.
 *   ATTRIBUTION   canManageApp(caller, appTenant) — a tenant-admin is a board
 *                 principal over ITS OWN tenant's apps. Before F6 only the role
 *                 was asked, and a tenant-admin of one tenant got the full shell
 *                 (including an active "send to review") on every other tenant's
 *                 app. See the guard in the body for the whole account.
 * APP_OPERATIONS_ROLES itself is unchanged
 * (decision-2026-08-03-role-model-planes; broker-operator left this list on
 * 2026-08-02, service-admin and vertical-product-owner on 2026-08-03). It does
 * NOT mirror AdminPage: AdminPage admits broker-admin and no tenant-admin. The
 * board is deliberately the wider set — it is the tenant admin's own surface
 * (adr-075 D11).
 */

import { useState, type ReactNode } from 'react';
import { useAuth } from '../auth/KeycloakProvider';
import { APP_OPERATIONS_ROLES, canManageApp, type CallerIdentity } from '../auth/surfaceAccess';
import { isCatalogOperator } from '../auth/catalogOperator';
import { t } from '../i18n';
import EditDescriptionModal from '../components/EditDescriptionModal';
import DeployRolloutModal from '../components/DeployRolloutModal';
import {
  useDeployments, useBranchState, useAppHealth, useVerifyStatus,
  useAccessList, useBoardAction, useAppSummary, useAppBilling,
  type BoardDeployment,
} from '../hooks/useAppOperations';

const BOARD_UI_ROLES = APP_OPERATIONS_ROLES;

// ── Small presentational helpers ─────────────────────────────────────────────

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-6)',
        marginBottom: 'var(--sp-6)',
      }}
    >
      <h2 style={{ fontSize: 'var(--fs-h3)', margin: '0 0 var(--sp-4) 0' }}>{title}</h2>
      {children}
    </section>
  );
}

function StatusPill({ kind, label }: { kind: 'good' | 'warn' | 'bad' | 'muted'; label: string }) {
  const palette: Record<string, { bg: string; fg: string }> = {
    good:  { bg: 'var(--dp-green-100)',  fg: 'var(--dp-green-800)' },
    warn:  { bg: 'var(--dp-amber-100)',  fg: 'var(--dp-amber-800)' },
    bad:   { bg: 'var(--dp-crimson-100)', fg: 'var(--dp-crimson-800)' },
    muted: { bg: 'var(--dp-canvas-200)',           fg: 'var(--fg-3)' },
  };
  const c = palette[kind];
  return (
    <span style={{
      background: c.bg, color: c.fg, borderRadius: 'var(--radius-pill)',
      padding: '2px 10px', fontSize: 'var(--fs-meta)', fontFamily: 'var(--font-sans)',
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function InlineNote({ children }: { children: ReactNode }) {
  return (
    <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', margin: 'var(--sp-2) 0 0 0' }}>
      {children}
    </p>
  );
}

/** Rollout status → non-technical label + pill kind (D8 vocabulary). */
function statusView(d: BoardDeployment): { label: string; kind: 'good' | 'warn' | 'bad' | 'muted' } {
  if (d.status === 'uat' && d.queued) return { label: t('opsBoard.deployments.status.queued'), kind: 'muted' };
  switch (d.status) {
    case 'uat':                   return { label: t('opsBoard.deployments.status.inReview'),  kind: 'warn' };
    case 'published':             return { label: t('opsBoard.deployments.status.live'),      kind: 'good' };
    case 'pushed_back':           return { label: t('opsBoard.deployments.status.sentBack'),  kind: 'bad' };
    case 'superseded_published':  return { label: t('opsBoard.deployments.status.replaced'),  kind: 'muted' };
    default:                      return { label: d.status, kind: 'muted' };
  }
}

function AccessDenied({ body }: { body?: string } = {}) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 'var(--sp-3)', padding: 'var(--sp-12)' }}>
      <p className="dp-eyebrow">{t('opsBoard.denied.eyebrow')}</p>
      <h2>{t('opsBoard.denied.title')}</h2>
      <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
        {body ?? t('opsBoard.denied.body')}
      </p>
    </div>
  );
}

/**
 * Attribution is not yet known (still fetching) or could not be read (the
 * catalogue call failed). Either way the SHELL DOES NOT RENDER. Rendering it
 * "until we know" is exactly the defect F6 records: the board shell, App
 * details, Development branches, App health and an ACTIVE review button all
 * appeared for a caller who owned none of it, because the page had already
 * decided it was allowed on the strength of a role.
 */
function BoardGateNotice({ text }: { text: string }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 'var(--sp-3)', padding: 'var(--sp-12)' }}>
      <p className="dp-eyebrow">{t('opsBoard.eyebrow')}</p>
      <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>{text}</p>
    </div>
  );
}

function LoadFailed({ what }: { what: string }) {
  return <InlineNote>{t('opsBoard.loadFailed').replace('{{what}}', what)}</InlineNote>;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function AppOperationsPage({ appId }: { appId: string }) {
  const { user, token, hasRole } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  const deployments = useDeployments(appId, token, refreshKey);
  const branches = useBranchState(appId, token, refreshKey);
  const health = useAppHealth(appId, token, refreshKey);
  const verify = useVerifyStatus(appId, token, refreshKey);
  const access = useAccessList(appId, token, refreshKey);
  const summary = useAppSummary(appId, token, refreshKey);
  const billing = useAppBilling(appId, token, refreshKey);
  const { state: action, run } = useBoardAction(appId, token, bump);

  const [crComment, setCrComment] = useState('');
  const [crDeployNo, setCrDeployNo] = useState<number | null>(null);
  const [assignUser, setAssignUser] = useState('');
  const [assignGrant, setAssignGrant] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);

  const allowed = user && BOARD_UI_ROLES.some((r) => hasRole(r));
  if (!allowed) return <AccessDenied />;

  /**
   * F6 (2026-08-03), matrix cell V-taacme-9 — THE ROUTE GUARD IS TWO QUESTIONS.
   *
   * The line above answers "may this caller use a board at all". It never
   * answered "is THIS app theirs", and until now nothing on this page did:
   * MarketplacePage bounded the Manage affordance with canManageApp() per tile,
   * and the page it points at bounded nothing. A tenant-admin of `acme` who
   * typed (or kept) the URL of an `e2e` app got the whole shell, with several
   * panels 403ing underneath — offered-then-denied, the failure
   * decision-2026-08-02-rail-absent-not-denied exists to prevent.
   *
   * decision-2026-08-03-role-model-planes principle 4: roles grant capability,
   * claims scope it. Both halves are asked here, through the SAME imported
   * predicate the tile uses (canManageApp) — never re-written inline, because
   * the 2026-08-02 rail drift is why that rule exists.
   *
   * The app's tenant comes from the catalogue row the board already reads for
   * App details, so this costs no extra request. While that row is unknown the
   * shell does not render: a gate that renders optimistically is not a gate.
   * The server enforces the same predicate on every endpoint regardless
   * (services/service-broker board.ts requireAppScope) — this half only decides
   * what is OFFERED.
   */
  const caller: CallerIdentity = { company: user.company, hasRole };
  if (summary.tag === 'loading') return <BoardGateNotice text={t('opsBoard.loading')} />;
  if (summary.tag === 'error') return <BoardGateNotice text={t('opsBoard.denied.scopeUnknown')} />;
  if (!canManageApp(caller, summary.data?.tenant)) {
    return <AccessDenied body={t('opsBoard.denied.bodyOtherTenant')} />;
  }

  /**
   * The MUTATION predicate for the two relocated controls. Unchanged from when
   * they lived on the tile — policy-2026-08-01-tenant-admin-never-creates-apps
   * as refined by decision-2026-08-02-tenant-admin-scope-is-registry-mutation:
   * re-describing and deploying are registry mutation, which is a developer and
   * operator verb. A tenant-admin reaches this page as a first-class user and
   * still does not get these two controls. The server 403s regardless of what
   * renders here; this only decides whether a button appears at all
   * (decision-2026-08-02-rail-absent-not-denied).
   *
   * Whether a tenant-admin should own their own app's CATALOGUE DESCRIPTION is
   * an open question the scope ruling deliberately left open. It is a
   * server-side authZ change, so it is flagged here and not assumed.
   */
  const canMutateRegistry = isCatalogOperator(user.roles);

  const busy = action.tag === 'submitting';

  // Change-requestable rows: sent-back or live (the change-loop entry points).
  const crCandidates = deployments.tag === 'loaded'
    ? deployments.data.deployments.filter((d) => d.status === 'pushed_back' || d.status === 'published')
    : [];
  const queueRows = deployments.tag === 'loaded'
    ? deployments.data.deployments.filter((d) => d.status === 'uat')
    : [];

  return (
    <div className="dp-shell__content" style={{ maxWidth: 960, margin: '0 auto', padding: 'var(--sp-6)' }}>
      <p className="dp-eyebrow">{t('opsBoard.eyebrow')}</p>
      <h1 style={{ margin: '0 0 var(--sp-2) 0' }}>{t('opsBoard.title').replace('{{app}}', appId)}</h1>
      <InlineNote>{t('opsBoard.intro')}</InlineNote>
      <div style={{ height: 'var(--sp-6)' }} />

      {/* Action feedback strip — declared results, never silent */}
      {action.tag === 'done' && (
        <div role="status" style={{ background: 'var(--dp-green-100)', borderRadius: 8, padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)', fontSize: 'var(--fs-body-sm)' }}>
          {action.message ?? t('opsBoard.actionDone')}
        </div>
      )}
      {action.tag === 'error' && (
        <div role="alert" style={{ background: 'var(--dp-crimson-100)', borderRadius: 8, padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)', fontSize: 'var(--fs-body-sm)' }}>
          {t('opsBoard.actionFailed').replace('{{reason}}', action.message)}
        </div>
      )}

      {/* ── 0. App details — the relocated Edit description (WP-2) ──────────── */}
      <SectionCard title={t('opsBoard.details.title')}>
        {/* F6: the page-level scope gate above already returned for `loading`
            and for `error` — the board never renders while the app's tenant is
            unknown — so by here the catalogue row is decided. A row that is
            genuinely absent is still a state, and still says so. */}
        {summary.data === null && (
          <InlineNote>{t('opsBoard.details.notFound')}</InlineNote>
        )}
        {summary.data && (
          <>
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-3)', margin: 0 }}>
              {t('opsBoard.details.descriptionLabel')}
            </p>
            <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--fg-1)', margin: 'var(--sp-2) 0 0 0' }}>
              {summary.data.description || t('opsBoard.details.descriptionEmpty')}
            </p>
            {canMutateRegistry ? (
              <div style={{ marginTop: 'var(--sp-4)' }}>
                <button className="dp-btn" onClick={() => setShowEditModal(true)}>
                  {t('opsBoard.details.edit')}
                </button>
                {/*
                  The endpoint behind this button does not exist. Not "is not
                  reachable from here" — PATCH /registry/services/:id has never
                  been implemented in the service-broker, which the modal's own
                  header has said since brief-17. Relocating the control from the
                  tile to this board neither created nor broke that; the button
                  was equally dead where it used to live.
                  It is kept because this brief's WP-2 is a RELOCATION and
                  silently dropping a control while claiming to move it would
                  hide the gap rather than close it. But an operator meeting a
                  raw 404 and concluding the board is broken is the failure mode
                  worth spending three lines to prevent, so the gap is stated
                  where they will read it. Wiring the endpoint or removing the
                  control is a decision, and it is recorded as one.
                */}
                <InlineNote>{t('opsBoard.details.notWired')}</InlineNote>
              </div>
            ) : (
              /* Read-only, and SAID so. A section that simply omits its control
                 reads as a rendering bug; naming why is the difference between
                 absent-not-denied and a surface that just looks broken. */
              <InlineNote>{t('opsBoard.details.readOnlyNote')}</InlineNote>
            )}
          </>
        )}
      </SectionCard>

      {/* ── 1. Version history + review queue (D8, D10) ────────────────────── */}
      <SectionCard title={t('opsBoard.deployments.title')}>
        {deployments.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {deployments.tag === 'error' && <LoadFailed what={t('opsBoard.deployments.title')} />}
        {deployments.tag === 'loaded' && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--fg-3)' }}>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.deployments.colVersion')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.deployments.colStatus')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.deployments.colDecision')}</th>
                </tr>
              </thead>
              <tbody>
                {deployments.data.deployments.map((d) => {
                  const sv = statusView(d);
                  return (
                    <tr key={d.deploy_no} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: 'var(--sp-2)' }}>
                        <strong>{t('opsBoard.deployments.versionLabel').replace('{{no}}', String(d.deploy_no)).replace('{{date}}', d.deploy_date)}</strong>
                        <div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>{d.release_tag}</div>
                      </td>
                      <td style={{ padding: 'var(--sp-2)' }}><StatusPill kind={sv.kind} label={sv.label} /></td>
                      <td style={{ padding: 'var(--sp-2)', color: 'var(--fg-2)' }}>
                        {d.decided_by
                          ? <>
                              {t('opsBoard.deployments.decidedBy').replace('{{when}}', d.decided_at ? new Date(d.decided_at).toLocaleDateString() : '—')}
                              {d.decision_comment && <div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>&ldquo;{d.decision_comment}&rdquo;</div>}
                            </>
                          : <span style={{ color: 'var(--fg-3)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
                {deployments.data.deployments.length === 0 && (
                  <tr><td colSpan={3} style={{ padding: 'var(--sp-4)', color: 'var(--fg-3)' }}>{t('opsBoard.deployments.empty')}</td></tr>
                )}
              </tbody>
            </table>

            {/* Review queue (D10): the single slot + who is waiting */}
            <h3 style={{ fontSize: 'var(--fs-body)', margin: 'var(--sp-5) 0 var(--sp-2) 0' }}>{t('opsBoard.queue.title')}</h3>
            {queueRows.length === 0 && <InlineNote>{t('opsBoard.queue.empty')}</InlineNote>}
            {queueRows.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 'var(--sp-5)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
                {queueRows.map((d) => (
                  <li key={d.deploy_no} style={{ marginBottom: 'var(--sp-1)' }}>
                    {t('opsBoard.deployments.versionLabel').replace('{{no}}', String(d.deploy_no)).replace('{{date}}', d.deploy_date)}
                    {' — '}
                    {d.queued ? t('opsBoard.queue.waiting') : t('opsBoard.queue.activeSlot')}
                  </li>
                ))}
              </ul>
            )}
            <InlineNote>{t('opsBoard.queue.note')}</InlineNote>
          </>
        )}

        {/* The relocated Deploy rollout (WP-2). It belongs in version history
            because that is where deployments already live — a rollout is the
            act that creates the next row of the table above it. */}
        <div style={{ marginTop: 'var(--sp-5)', paddingTop: 'var(--sp-4)', borderTop: '1px solid var(--border-subtle)' }}>
          {canMutateRegistry ? (
            <button
              className="dp-btn"
              disabled={summary.tag !== 'loaded' || summary.data === null}
              onClick={() => setShowDeployModal(true)}
            >
              {t('opsBoard.deploy.action')}
            </button>
          ) : (
            <InlineNote>{t('opsBoard.deploy.note')}</InlineNote>
          )}
        </div>
      </SectionCard>

      {/* ── 2. Development branches ────────────────────────────────────────── */}
      <SectionCard title={t('opsBoard.branches.title')}>
        {branches.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {branches.tag === 'error' && <LoadFailed what={t('opsBoard.branches.title')} />}
        {branches.tag === 'loaded' && (
          branches.data.branches.length === 0
            ? <InlineNote>{t('opsBoard.branches.empty')}</InlineNote>
            : (
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
                {branches.data.branches.map((b) => (
                  <li key={b.branch} style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', padding: 'var(--sp-2) 0', borderTop: '1px solid var(--border-subtle)' }}>
                    <code>{b.branch}</code>
                    <StatusPill kind={b.open ? 'warn' : 'muted'} label={b.open ? t('opsBoard.branches.open') : t('opsBoard.branches.closed')} />
                  </li>
                ))}
              </ul>
            )
        )}
      </SectionCard>

      {/* ── 3. Health & verification (Part B) ─────────────────────────────── */}
      <SectionCard title={t('opsBoard.health.title')}>
        {health.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {health.tag === 'error' && <LoadFailed what={t('opsBoard.health.title')} />}
        {health.tag === 'loaded' && (
          <>
            <div style={{ marginBottom: 'var(--sp-3)' }}>
              <StatusPill
                kind={health.data.overall === 'healthy' ? 'good' : health.data.overall === 'degraded' ? 'warn' : 'bad'}
                label={t(`opsBoard.health.overall.${health.data.overall}` as Parameters<typeof t>[0])}
              />
            </div>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
              {health.data.checks.map((c) => (
                <li key={c.name} style={{ display: 'flex', gap: 'var(--sp-3)', padding: 'var(--sp-1) 0' }}>
                  <span style={{ minWidth: 120 }}>{c.name}</span>
                  <StatusPill kind={c.ok ? 'good' : 'bad'} label={c.ok ? t('opsBoard.health.ok') : t('opsBoard.health.failed')} />
                  {c.latency_ms !== null && <span style={{ color: 'var(--fg-3)' }}>{c.latency_ms} ms</span>}
                  {c.detail && <span style={{ color: 'var(--fg-3)' }}>{c.detail}</span>}
                </li>
              ))}
            </ul>
          </>
        )}
        <div style={{ marginTop: 'var(--sp-4)', display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
          <button className="dp-btn" disabled={busy} onClick={() => void run('verify', '/verify')}>
            {busy && action.tag === 'submitting' && action.action === 'verify' ? t('opsBoard.working') : t('opsBoard.health.runVerification')}
          </button>
          {verify.tag === 'loaded' && verify.data.last && (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-2)' }}>
              {verify.data.last.result
                ? t('opsBoard.health.lastVerify')
                    .replace('{{pass}}', String(verify.data.last.result.pass))
                    .replace('{{fail}}', String(verify.data.last.result.fail))
                : t('opsBoard.health.verifyRunning')}
              {verify.data.last.result && verify.data.last.result.failed_gates.length > 0 &&
                ` (${verify.data.last.result.failed_gates.join(', ')})`}
            </span>
          )}
        </div>
      </SectionCard>

      {/* ── 4. Changes & review (Part C triggers) ─────────────────────────── */}
      <SectionCard title={t('opsBoard.changes.title')}>
        <InlineNote>{t('opsBoard.changes.intro')}</InlineNote>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-4)', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={crDeployNo ?? ''}
            onChange={(e) => setCrDeployNo(e.target.value ? Number(e.target.value) : null)}
            style={{ padding: 'var(--sp-2)', fontFamily: 'var(--font-sans)' }}
            aria-label={t('opsBoard.changes.pickVersion')}
          >
            <option value="">{t('opsBoard.changes.pickVersion')}</option>
            {crCandidates.map((d) => (
              <option key={d.deploy_no} value={d.deploy_no}>
                {t('opsBoard.deployments.versionLabel').replace('{{no}}', String(d.deploy_no)).replace('{{date}}', d.deploy_date)}
                {' — '}{statusView(d).label}
              </option>
            ))}
          </select>
          <input
            value={crComment}
            onChange={(e) => setCrComment(e.target.value)}
            placeholder={t('opsBoard.changes.commentPlaceholder')}
            style={{ flex: 1, minWidth: 220, padding: 'var(--sp-2)', fontFamily: 'var(--font-sans)' }}
          />
          <button
            className="dp-btn"
            disabled={busy || crDeployNo === null}
            onClick={() => void run('change-request', '/change-request', { body: { deploy_no: crDeployNo, ...(crComment ? { comment: crComment } : {}) } })}
          >
            {t('opsBoard.changes.openChangeRequest')}
          </button>
          <button
            className="dp-btn"
            disabled={busy}
            onClick={() => void run('send-to-uat', '/send-to-uat')}
          >
            {t('opsBoard.changes.sendToReview')}
          </button>
        </div>
        <InlineNote>{t('opsBoard.changes.reviewNote')}</InlineNote>
      </SectionCard>

      {/* ── 5. Access — who is assigned to this app (WP-3) ─────────────────── */}
      <SectionCard title={t('opsBoard.access.title')}>
        {/* The realm-wide caveat is NOT removed by tenant scoping. Scoping bounds
            who this board will OFFER and accept; the grant Keycloak actually
            writes is still realm-wide, and hiding that would be the more
            comfortable lie. Both notes stay. */}
        <InlineNote>{t('opsBoard.access.realmNote')}</InlineNote>
        {access.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {access.tag === 'error' && <LoadFailed what={t('opsBoard.access.title')} />}
        {access.tag === 'loaded' && (
          <>
            <InlineNote>
              {access.data.app_tenant
                ? t('opsBoard.access.appTenant').replace('{{tenant}}', access.data.app_tenant)
                : t('opsBoard.access.appTenantUnknown')}
            </InlineNote>
            <InlineNote>{access.data.tenant_note}</InlineNote>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', marginTop: 'var(--sp-4)' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--fg-3)' }}>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.access.colUser')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.access.colTenant')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.access.colApprover')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.access.colTester')}</th>
                </tr>
              </thead>
              <tbody>
                {access.data.users.map((u) => (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: 'var(--sp-2)' }}>{u.username}<div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>{u.email}</div></td>
                    <td style={{ padding: 'var(--sp-2)', color: u.tenant ? 'var(--fg-2)' : 'var(--fg-3)' }}>
                      {u.tenant ?? t('opsBoard.access.tenantUnknown')}
                    </td>
                    {(['uat-approver', 'tester'] as const).map((role) => {
                      const has = role === 'uat-approver' ? u.uat_approver : u.tester;
                      return (
                        <td key={role} style={{ padding: 'var(--sp-2)' }}>
                          <button
                            className="dp-btn dp-btn--ghost"
                            disabled={busy}
                            onClick={() => void run(
                              `access-${has ? 'remove' : 'grant'}`,
                              `/access/${role}`,
                              { method: has ? 'DELETE' : 'POST', body: { username: u.username } },
                            )}
                          >
                            {has ? t('opsBoard.access.remove') : t('opsBoard.access.grant')}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {access.data.users.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 'var(--sp-4)', color: 'var(--fg-3)' }}>{t('opsBoard.access.noneAssignable')}</td></tr>
                )}
              </tbody>
            </table>

            {/* Assign: a PICKER over exactly the set the server will accept, not a
                free-text username box. The old input let anyone type any name and
                learn from the 403 which names exist; the server's assignable list
                and this dropdown are the same population by construction, so the
                UI cannot offer someone the server would refuse. */}
            <h3 style={{ fontSize: 'var(--fs-body)', margin: 'var(--sp-5) 0 var(--sp-2) 0' }}>{t('opsBoard.access.assignTitle')}</h3>
            {access.data.assignable.length === 0 ? (
              <InlineNote>{t('opsBoard.access.noneAssignable')}</InlineNote>
            ) : (
              <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={assignUser}
                  onChange={(e) => setAssignUser(e.target.value)}
                  aria-label={t('opsBoard.access.assignPick')}
                  style={{ padding: 'var(--sp-2)', fontFamily: 'var(--font-sans)', minWidth: 220 }}
                >
                  <option value="">{t('opsBoard.access.assignPick')}</option>
                  {access.data.assignable.map((u) => (
                    <option key={u.id} value={u.username}>
                      {u.username}{u.email ? ` — ${u.email}` : ''}
                    </option>
                  ))}
                </select>
                <select
                  value={assignGrant}
                  onChange={(e) => setAssignGrant(e.target.value)}
                  aria-label={t('opsBoard.access.assignRole')}
                  style={{ padding: 'var(--sp-2)', fontFamily: 'var(--font-sans)' }}
                >
                  <option value="">{t('opsBoard.access.assignRole')}</option>
                  {access.data.assignable_grants.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                <button
                  className="dp-btn"
                  disabled={busy || !assignUser || !assignGrant}
                  onClick={() => void run('access-grant', `/access/${assignGrant}`, { body: { username: assignUser } })}
                >
                  {t('opsBoard.access.assign')}
                </button>
                <button
                  className="dp-btn dp-btn--ghost"
                  disabled={busy || !assignUser || !assignGrant}
                  onClick={() => void run('access-remove', `/access/${assignGrant}`, { method: 'DELETE', body: { username: assignUser } })}
                >
                  {t('opsBoard.access.unassign')}
                </button>
              </div>
            )}
            <InlineNote>
              {t('opsBoard.access.grantsNote').replace('{{grants}}', access.data.assignable_grants.join(', '))}
            </InlineNote>
            {(access.data.hidden.other_tenant > 0 || access.data.hidden.unattributed > 0) && (
              /* Counted, not silently dropped: a list that shows six of nine and
                 says nothing is lying by omission. */
              <InlineNote>
                {t('opsBoard.access.hiddenNote')
                  .replace('{{other}}', String(access.data.hidden.other_tenant))
                  .replace('{{unattributed}}', String(access.data.hidden.unattributed))}
              </InlineNote>
            )}
          </>
        )}
      </SectionCard>

      {/* ── 6. Billing & subscription (WP-4) ────────────────────────────────── */}
      <SectionCard title={t('opsBoard.billing.title')}>
        <InlineNote>{t('opsBoard.billing.intro')}</InlineNote>
        {billing.tag === 'loading' && <InlineNote>{t('opsBoard.loading')}</InlineNote>}
        {billing.tag === 'error' && <LoadFailed what={t('opsBoard.billing.title')} />}
        {billing.tag === 'loaded' && (
          <>
            {/*
              THE EMPTY SLOT, STATED.
              open-task-2026-07-25-cost-disclosure-slot-empty is open: this
              platform has no rate model. `rate_model` is null by construction on
              the server, so there is nothing here to format and nothing to round.
              Rendering a figure — even a zero, even a "—" that reads as one —
              would repeat decision-2026-07-25-remove-fabricated-trial-cost. The
              honest empty state IS the deliverable for this part.
            */}
            <div style={{
              background: 'var(--bg-recessed)', border: '1px dashed var(--border-default)',
              borderRadius: 'var(--radius-md)', padding: 'var(--sp-4)', margin: 'var(--sp-4) 0',
            }}>
              <StatusPill kind="muted" label={t('opsBoard.billing.noRateModel')} />
              <InlineNote>{t('opsBoard.billing.noRateModelBody')}</InlineNote>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--fg-3)' }}>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.billing.colPlan')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.billing.colStatus')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.billing.colTenant')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.billing.colWho')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.billing.colStarted')}</th>
                  <th style={{ padding: 'var(--sp-2)' }}>{t('opsBoard.billing.colSeats')}</th>
                </tr>
              </thead>
              <tbody>
                {billing.data.subscriptions.map((sub) => (
                  <tr key={sub.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: 'var(--sp-2)' }}>
                      {sub.plan_name ?? sub.plan_id}
                      {sub.trial_ends_at && (
                        <div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>
                          {t('opsBoard.billing.trialEnds').replace('{{when}}', new Date(sub.trial_ends_at).toLocaleDateString())}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 'var(--sp-2)' }}>
                      <StatusPill
                        kind={sub.status === 'active' ? 'good' : sub.status === 'ended' || sub.status === 'rejected' ? 'muted' : 'warn'}
                        label={sub.status}
                      />
                      {sub.billing_status && (
                        <div style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>
                          {sub.billing_status}
                          {sub.notice_period_days !== null && sub.notice_period_days > 0 &&
                            ` · ${t('opsBoard.billing.noticeDays').replace('{{days}}', String(sub.notice_period_days))}`}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 'var(--sp-2)', color: sub.tenant_name ? 'var(--fg-2)' : 'var(--fg-3)' }}>
                      {sub.tenant_name ?? t('opsBoard.access.tenantUnknown')}
                    </td>
                    <td style={{ padding: 'var(--sp-2)', color: 'var(--fg-2)' }}>{sub.user_email ?? '—'}</td>
                    <td style={{ padding: 'var(--sp-2)', color: 'var(--fg-2)' }}>
                      {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : '—'}
                    </td>
                    {/* seats is null by construction. Counting subscription rows
                        and calling the result seats would be an invented number
                        of exactly the kind this section refuses. */}
                    <td style={{ padding: 'var(--sp-2)', color: 'var(--fg-3)' }}>{t('opsBoard.billing.seatsUnknown')}</td>
                  </tr>
                ))}
                {billing.data.subscriptions.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 'var(--sp-4)', color: 'var(--fg-3)' }}>{t('opsBoard.billing.empty')}</td></tr>
                )}
              </tbody>
            </table>

            <h3 style={{ fontSize: 'var(--fs-body)', margin: 'var(--sp-5) 0 var(--sp-2) 0' }}>{t('opsBoard.billing.eventsTitle')}</h3>
            {billing.data.events.length === 0 && <InlineNote>{t('opsBoard.billing.eventsEmpty')}</InlineNote>}
            {billing.data.events.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 'var(--sp-5)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
                {billing.data.events.map((e, i) => (
                  <li key={`${e.at}-${i}`} style={{ marginBottom: 'var(--sp-1)' }}>
                    <span style={{ color: 'var(--fg-3)' }}>{new Date(e.at).toLocaleDateString()}</span>
                    {' — '}{e.event}{e.detail ? ` (${e.detail})` : ''}
                  </li>
                ))}
              </ul>
            )}
            <InlineNote>{t('opsBoard.billing.cancelNote')}</InlineNote>
          </>
        )}
      </SectionCard>

      {/* ── Danger zone — a RESERVED SLOT, deliberately empty ───────────────── */}
      {/*
        decision-2026-08-02-delete-ui-under-tile already places decommission on
        this surface, and its own brief builds it. This brief only reserves the
        space, for one reason: an unreserved decommission has to land somewhere,
        and the place it used to land was the catalogue tile. Nothing is wired
        here — no control, no dark flag, no endpoint call. If it looks like an
        empty box, that is exactly what it is.
      */}
      <section
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--dp-crimson-300, var(--border-default))',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--sp-6)',
          marginBottom: 'var(--sp-6)',
          marginTop: 'var(--sp-8)',
        }}
      >
        <h2 style={{ fontSize: 'var(--fs-h3)', margin: '0 0 var(--sp-4) 0', color: 'var(--dp-crimson-600, var(--fg-1))' }}>
          {t('opsBoard.danger.title')}
        </h2>
        <InlineNote>{t('opsBoard.danger.reserved')}</InlineNote>
      </section>

      {/* Relocated modals — mounted by the BOARD now, never by a tile. */}
      {showEditModal && summary.tag === 'loaded' && summary.data && (
        <EditDescriptionModal
          tile={summary.data}
          onClose={() => setShowEditModal(false)}
          onSaved={bump}
        />
      )}
      {showDeployModal && summary.tag === 'loaded' && summary.data && (
        <DeployRolloutModal tile={summary.data} onClose={() => setShowDeployModal(false)} />
      )}
    </div>
  );
}
